import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SeoHead } from "@/components/SeoHead";
import { staggerContainer, fadeUp } from "@/lib/motion";
import { useContent } from "@/context/ContentContext";

export default function Donanim() {
  const { t } = useTranslation();
  const { media } = useContent();
  const products = t("hardware.products", { returnObjects: true });

  const images = [media.growbot, media.multidoser, media.vacuumDryer, media.diyKit];

  return (
    <div data-testid="donanim-page" className="py-20 md:py-28">
      <SeoHead pageKey="hardware" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* HEADER */}
        <Reveal>
          <p className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-5">
            {t("hardware.overline")}
          </p>
          <h1 className="font-display font-bold text-[#1a3636] text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-none max-w-4xl">
            {t("hardware.title")}
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#1a3636]/65 leading-relaxed max-w-2xl">
            {t("hardware.sub")}
          </p>
        </Reveal>

        {/* PRODUCTS GRID */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
        >
          {products.map((product, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              data-testid={`hardware-card-${i}`}
              className="group bg-white rounded-2xl overflow-hidden border border-[#1a3636]/5 shadow-[0_8px_30px_rgb(26,54,54,0.06)] hover:shadow-[0_20px_40px_rgb(43,138,126,0.12)] hover:-translate-y-1 transition-all duration-500"
            >
              {/* ==== PRODUCT IMAGE (swap src if needed) ==== */}
              <div className="relative h-72 md:h-80 overflow-hidden bg-[#f0f4f3]">
                <img
                  src={images[i]}
                  alt={product.name}
                  data-testid={`hardware-image-${i}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 bg-[#1a3636]/85 backdrop-blur text-[#f8faf9] px-4 py-1.5 rounded-full text-xs uppercase tracking-widest">
                  0{i + 1}
                </span>
              </div>
              <div className="p-8">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display text-2xl font-semibold text-[#1a3636] tracking-tight">
                    {product.name}
                  </h3>
                  <ArrowUpRight
                    size={22}
                    strokeWidth={1.5}
                    className="text-[#2b8a7e] shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>
                <p className="mt-3 text-[#1a3636]/60 leading-relaxed">{product.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
